<template lang="pug">
  button.yz-btn(:type="type" :class="buttonClasses" :style="buttonStyle" :disabled="disabled || loading" @click="handleClick")
    .yz-btn__flash(v-if="showFlash" :style="flashStyle")
    .yz-btn__content(:class="{ 'yz-btn__content--loading': loading }")
      .yz-btn__icon.yz-btn__icon--prepend(v-if="prependIcon")
        yz-icon(:size="getIconSize" :color="computedIconColor") {{ prependIcon }}
      .yz-btn__label(v-if="label" :class="labelClass") {{ label }}
      yz-icon(v-if="icon" :size="getIconSize" :color="computedIconColor") {{ icon }}
      .yz-btn__icon.yz-btn__icon--append(v-if="appendIcon")
        yz-icon(:size="getIconSize" :color="computedIconColor") {{ appendIcon }}
    .yz-btn__loader(v-if="loading")
      .yz-btn__spinner
</template>

<script>
export default {
  data() {
    return {
      showFlash: false,
      flashTimeout: null
    }
  },
  props: {
    label: {
      type: String,
      default: null
    },
    color: {
      type: String,
      default: 'primary'
    },
    textColor: {
      type: String,
      default: null
    },
    backgroundColor: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md'
      // sm, md, lg
    },
    height: {
      type: String,
      default: null
    },
    width: {
      type: String,
      default: null
    },
    appendIcon: {
      type: String,
      default: null
    },
    prependIcon: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    iconSize: {
      default: null
    },
    iconColor: {
      type: String,
      default: null
    },
    dark: {
      type: Boolean,
      default: false
    },
    underline: {
      type: Boolean,
      default: false
    },
    text: {
      type: Boolean,
      default: false
    },
    buttonClass: {
      type: String,
      default: null
    },
    unsetMinWidth: {
      type: Boolean,
      default: false
    },
    iconOnly: {
      type: Boolean,
      default: false
    },
    secondary: {
      type: Boolean,
      default: false
    },
    disableRipple: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'button'
    }
  },
  computed: {
    buttonClasses() {
      const classes = []

      // Size class
      classes.push(`yz-btn--${this.size}`)

      // Color/variant classes
      if (this.outlined) {
        classes.push('yz-btn--outlined')
      } else if (this.text || this.underline) {
        classes.push('yz-btn--text')
      } else if (this.secondary) {
        classes.push('yz-btn--secondary')
      } else {
        classes.push('yz-btn--filled')
      }

      // Color classes
      if (this.color === 'primary' || this.color === 'danger') {
        classes.push(`yz-btn--${this.color}`)
      } else if (this.color && this.color.startsWith('#')) {
        // Hex color will be handled in buttonStyle computed property
      } else {
        classes.push(`bg-${this.color}`)
      }

      // State classes
      if (this.disabled) {
        classes.push('yz-btn--disabled')
      }
      if (this.loading) {
        classes.push('yz-btn--loading')
      }
      if (this.block) {
        classes.push('yz-btn--block')
      }
      if (this.iconOnly) {
        classes.push('yz-btn--icon-only')
      }
      if (this.underline) {
        classes.push('yz-btn--underline')
      }

      // Custom class
      if (this.buttonClass) {
        classes.push(this.buttonClass)
      }

      return classes
    },
    labelClass() {
      const classes = []

      // Typography class based on size
      if (this.size === 'sm') {
        classes.push('yz-medium-body-xs')
      } else {
        classes.push('yz-medium-body-sm')
      }

      if (this.underline) {
        classes.push('underline-offset')
      }

      return classes.join(' ')
    },
    buttonStyle() {
      const style = {}

      if (this.backgroundColor) {
        style.backgroundColor = this.backgroundColor
      } else if (this.color && this.color.startsWith('#')) {
        style.backgroundColor = this.color
      }

      if (this.textColor && !this.disabled) {
        if (this.textColor.startsWith('#')) {
          style.color = this.textColor
        } else if (this.textColor.startsWith('yz-')) {
          style.color = `var(--${this.textColor})`
        } else {
          style.color = `var(--yz-${this.textColor})`
        }
      }

      // For icon buttons with no label
      if (this.icon) {
        style.padding = '6px'
        const buttonSizeMap = {
          sm: 24,
          md: 28,
          lg: 36,
          xl: 44
        }
        style.height = `${buttonSizeMap[this.size]}px`
        style.width = `${buttonSizeMap[this.size]}px`
      }

      if (this.height) {
        style.height = `${this.height}px`
      }

      if (this.width) {
        style.width = `${this.width}px`
      }

      if (this.unsetMinWidth || this.iconOnly || this.label === null || this.label === '') {
        style.minWidth = 'unset'
      }


      return style
    },
    computedIconColor() {
      // If iconColor prop is provided, use it with proper formatting
      if (this.iconColor) {
        if (this.iconColor.startsWith('#')) {
          return this.iconColor
        } else if (this.iconColor.startsWith('yz-')) {
          return `var(--${this.iconColor})`
        } else {
          return `var(--yz-${this.iconColor})`
        }
      }

      // If disabled, use neutral-400
      if (this.disabled) {
        return 'yz-neutral-400'
      }

      // Icon-only buttons always use neutral-700 by default
      if (this.iconOnly) {
        return 'yz-neutral-700'
      }

      // Determine color based on button variant and color
      if (this.color === 'primary') {
        if (this.outlined || this.text || this.underline || this.secondary) {
          return 'yz-neutral-700'
        } else {
          // Filled primary button
          return 'yz-white'
        }
      } else if (this.color === 'danger') {
        if (this.outlined || this.text || this.underline || this.secondary) {
          return 'yz-danger-500'
        } else {
          // Filled danger button
          return 'yz-white'
        }
      }

      // Default fallback
      return 'yz-neutral-700'
    },
    getIconSize() {
      if (this.iconSize) {
        return typeof this.iconSize === 'number' ? `${this.iconSize}px` : this.iconSize
      }

      if (this.iconOnly || this.icon) {
        const iconSizeMap = {
          sm: 12,
          md: 16,
          lg: 20,
          xl: 24
        }
        return iconSizeMap[this.size]
      }

      return 16
    },
    flashStyle() {
      // Determine flash color based on button variant and background color
      let flashColor = 'rgba(255, 255, 255, 0.3)' // Default: light flash for dark backgrounds

      // Light background variants (outlined, text, secondary, iconOnly) use dark flash
      if (this.outlined || this.text || this.underline || this.secondary || this.iconOnly) {
        flashColor = 'rgba(51, 51, 51, 0.1)'
      }

      // For filled buttons, determine flash color based on background color
      if (!this.outlined && !this.text && !this.underline && !this.secondary && !this.iconOnly) {
        // Check if we have a custom background color
        const bgColor = this.backgroundColor || (this.color && this.color.startsWith('#') ? this.color : null)
        
        if (bgColor) {
          // Custom color - determine if it's light or dark
          if (this.isLightColor(bgColor)) {
            flashColor = 'rgba(51, 51, 51, 0.15)' // Dark flash for light backgrounds
          } else {
            flashColor = 'rgba(255, 255, 255, 0.3)' // Light flash for dark backgrounds
          }
        } else if (this.color === 'primary') {
          // Primary (dark) - use light flash
          flashColor = 'rgba(255, 255, 255, 0.3)'
        } else if (this.color === 'danger') {
          // Danger (red) - use light flash
          flashColor = 'rgba(255, 255, 255, 0.3)'
        }
      }

      // Special case for danger variants with light backgrounds
      if (this.color === 'danger' && (this.outlined || this.text || this.secondary)) {
        flashColor = 'rgba(200, 47, 47, 0.15)'
      }

      return {
        backgroundColor: flashColor
      }
    }
  },
  methods: {
    handleClick(event) {
      // Emit click event
      this.$emit('click', event)
      // Create flash effect
      this.createFlash()
    },

    createFlash() {
      // Don't create flash if disabled or flash is disabled
      if (this.disabled || this.loading || this.disableRipple) return

      // Clear any existing timeout
      if (this.flashTimeout) {
        clearTimeout(this.flashTimeout)
      }

      // Reset flash state to ensure animation replays
      this.showFlash = false

      // Use nextTick to ensure DOM updates before showing flash again
      this.$nextTick(() => {
        this.showFlash = true

        // Remove flash after animation
        this.flashTimeout = setTimeout(() => {
          this.showFlash = false
          this.flashTimeout = null
        }, 300)
      })
    },
    isLightColor(color) {
      // Convert hex color to RGB and calculate brightness
      const hex = color.replace('#', '')
      const r = parseInt(hex.substring(0, 2), 16)
      const g = parseInt(hex.substring(2, 4), 16)
      const b = parseInt(hex.substring(4, 6), 16)

      // Calculate brightness using the standard formula
      const brightness = (r * 299 + g * 587 + b * 114) / 1000

      // Return true if the color is light (brightness > 128)
      return brightness > 128
    },
  },
  beforeDestroy() {
    // Clean up timeout on component destroy
    if (this.flashTimeout) {
      clearTimeout(this.flashTimeout)
    }
  },
}
</script>

<style scoped>
/* Base button styles */
.yz-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  outline: none;
  min-width: 64px;
  box-sizing: border-box;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}

.yz-btn:focus {
  outline: none;
}

/* Flash effect */
.yz-btn__flash {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: inherit;
  pointer-events: none;
  animation: flash-animation 0.3s ease-out;
  will-change: opacity;
}

@keyframes flash-animation {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

/* Button content layout */
.yz-btn__content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  z-index: 1;
  pointer-events: none;
  opacity: 1;
  transition: opacity 0.2s ease;
}

.yz-btn__content--loading {
  opacity: 0;
}

.yz-btn__icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.yz-btn__loader {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
}

.yz-btn__label {
  white-space: nowrap;
}

/* Size variants */
.yz-btn--sm {
  height: 24px;
  padding: 4px 8px;
}

.yz-btn--md {
  height: 28px;
  padding: 4px 8px;
}

.yz-btn--lg {
  height: 36px;
  padding: 8px 10px;
}

.yz-btn--xl {
  height: 44px;
  padding: 12px;
}

/* Icon only buttons */
.yz-btn--icon-only {
  background-color: transparent !important;
  border: none !important;
  padding: 0;
  border-radius: 50%;
  min-width: unset;
  color: var(--yz-neutral-700) !important;
}

.yz-btn--icon-only:hover:not(:disabled) {
  background-color: var(--yz-neutral-300) !important;
}

.yz-btn--icon-only.yz-btn--sm {
  width: 26px;
}

.yz-btn--icon-only.yz-btn--md {
  width: 30px;
}

.yz-btn--icon-only.yz-btn--lg {
  width: 40px;
}

.yz-btn--icon-only.yz-btn--xl {
  width: 48px;
}

/* Block button */
.yz-btn--block {
  width: 100%;
}

/* Color variants - Primary */
.yz-btn--filled.yz-btn--primary {
  background-color: var(--yz-neutral-700);
  color: white;
}

.yz-btn--filled.yz-btn--primary:hover:not(:disabled) {
  background-color: #545454;
}

.yz-btn--outlined.yz-btn--primary {
  background-color: transparent;
  border: 1px solid var(--yz-neutral-400);
  color: var(--yz-neutral-700);
}

.yz-btn--outlined.yz-btn--primary:hover:not(:disabled) {
  background-color: var(--yz-neutral-300);
}

.yz-btn--text.yz-btn--primary {
  background-color: transparent;
  color: var(--yz-neutral-700);
}

.yz-btn--text.yz-btn--primary:hover:not(:disabled) {
  background-color: var(--yz-neutral-300);
}

.yz-btn--secondary.yz-btn--primary {
  background-color: var(--yz-neutral-300);
  color: var(--yz-neutral-700);
}

.yz-btn--secondary.yz-btn--primary:hover:not(:disabled) {
  background-color: var(--yz-neutral-400);
}

/* Color variants - Danger */
.yz-btn--filled.yz-btn--danger {
  background-color: var(--yz-danger-500);
  color: var(--yz-white);
}

.yz-btn--filled.yz-btn--danger:hover:not(:disabled) {
  background-color: #E34F4F;
}

.yz-btn--outlined.yz-btn--danger {
  background-color: transparent;
  border: 1px solid var(--yz-neutral-400);
  color: var(--yz-danger-500);
}

.yz-btn--outlined.yz-btn--danger:hover:not(:disabled) {
  background-color: var(--yz-red-100);
}

.yz-btn--text.yz-btn--danger {
  background-color: transparent;
  color: var(--yz-danger-500);
}

.yz-btn--text.yz-btn--danger:hover:not(:disabled) {
  background-color: var(--yz-red-100);
}

.yz-btn--secondary.yz-btn--danger {
  background-color: var(--yz-neutral-300);
  color: var(--yz-danger-500);
}

.yz-btn--secondary.yz-btn--danger:hover:not(:disabled) {
  background-color: var(--yz-neutral-400);
}

/* Underline variant - apply only to label */
.yz-btn--underline .yz-btn__label {
  text-decoration: underline;
  text-underline-offset: 4px;
}

.underline-offset {
  text-underline-offset: 4px;
}

/* Disabled state */
.yz-btn:disabled,
.yz-btn--disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Loading state - override opacity even when :disabled is true */
.yz-btn--loading {
  cursor: not-allowed;
  opacity: 1 !important;
}

.yz-btn__spinner {
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.yz-btn--sm .yz-btn__spinner {
  width: 12px;
  height: 12px;
}

.yz-btn--md .yz-btn__spinner {
  width: 16px;
  height: 16px;
}

.yz-btn--lg .yz-btn__spinner {
  width: 20px;
  height: 20px;
}

.yz-btn--xl .yz-btn__spinner {
  width: 24px;
  height: 24px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>